import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Button,
  Card,
  CardMedia,
  Chip,
  Stack
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { PartInfo } from '../types';
import { useImagePreloader } from '../hooks/useImagePreloader';

interface PartImageCarouselProps {
  variants: PartInfo[];
  onVariantChange?: (variant: PartInfo) => void;
}

export const PartImageCarousel: React.FC<PartImageCarouselProps> = ({
  variants,
  onVariantChange
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const imageUrls = variants.map(variant => variant.imageUrl).filter(Boolean);
  useImagePreloader(imageUrls);

  useEffect(() => {
    setCurrentIndex(0);
  }, [variants]);

  useEffect(() => {
    if (onVariantChange && variants[currentIndex]) {
      onVariantChange(variants[currentIndex]);
    }
  }, [currentIndex, variants, onVariantChange]);

  const handlePrevious = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : variants.length - 1);
  };

  const handleNext = () => {
    setCurrentIndex(prev => prev < variants.length - 1 ? prev + 1 : 0);
  };

  const handleIndicatorClick = (index: number) => {
    setCurrentIndex(index);
  };

  if (variants.length === 0) return null;

  const currentVariant = variants[currentIndex];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Main Image Display */}
      <Box sx={{ position: 'relative', mb: 2 }}>
        <Card>
          <CardMedia
            component="img"
            image={currentVariant.imageUrl}
            alt={currentVariant.name}
            sx={{
              width: '100%',
              height: 400,
              objectFit: 'contain',
            }}
          />
        </Card>

        {/* Navigation Arrows */}
        {variants.length > 1 && (
          <>
            <IconButton
              onClick={handlePrevious}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: 2,
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                }
              }}
            >
              <ChevronLeft />
            </IconButton>

            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: 2,
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                }
              }}
            >
              <ChevronRight />
            </IconButton>
          </>
        )}

        {/* Variant count indicator */}
        {variants.length > 1 && (
          <Box sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            px: 2,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.875rem'
          }}>
            {currentIndex + 1} / {variants.length}
          </Box>
        )}
      </Box>

      {/* Variant Info */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Chip 
          label={`Image: ${currentVariant.imageId}`}
          size="small"
          variant="outlined"
        />
        <Chip 
          label={currentVariant.primaryOrOther}
          size="small"
          color={currentVariant.primaryOrOther === 'Primary' ? 'primary' : 'secondary'}
        />
        {variants.length > 1 && (
          <Chip 
            label={`${variants.length} variants`}
            size="small"
            color="info"
          />
        )}
      </Stack>

      {/* Thumbnail Navigation */}
      {variants.length > 1 && variants.length <= 6 && (
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          overflowX: 'auto',
          pb: 1
        }}>
          {variants.map((variant, index) => (
            <Button
              key={variant.imageId}
              onClick={() => handleIndicatorClick(index)}
              sx={{
                minWidth: 60,
                height: 60,
                p: 0,
                border: index === currentIndex ? '2px solid' : '1px solid',
                borderColor: index === currentIndex ? 'primary.main' : 'grey.300',
                borderRadius: 1,
                overflow: 'hidden',
                '&:hover': {
                  borderColor: 'primary.main',
                }
              }}
            >
              <img
                src={variant.imageUrl}
                alt={`Variant ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Button>
          ))}
        </Box>
      )}

      {/* Dot Indicators */}
      {variants.length > 6 && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 1, 
          mt: 2 
        }}>
          {variants.map((_, index) => (
            <Button
              key={index}
              onClick={() => handleIndicatorClick(index)}
              sx={{
                minWidth: 8,
                width: 8,
                height: 8,
                p: 0,
                borderRadius: '50%',
                bgcolor: index === currentIndex ? 'primary.main' : 'action.disabled',
                '&:hover': {
                  bgcolor: index === currentIndex ? 'primary.dark' : 'action.active',
                  transform: 'scale(1.2)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}; 